export const SELECTED_WORKSPACE_COOKIE_NAME = 'minerva-selected-workspace-id';
export const SELECTED_WORKSPACE_ID_STORAGE_KEY =
  'minerva:selected-workspace-id';
/**
 * Tenant the un-keyed `SELECTED_WORKSPACE_ID_STORAGE_KEY` value was written
 * for. That key is browser-wide and overwritten by whichever tenant selected
 * last, so readers with no tenant in scope cannot otherwise tell whose
 * workspace it holds. Kept in step with it on every write.
 */
export const SELECTED_WORKSPACE_TENANT_STORAGE_KEY =
  'minerva:selected-workspace-tenant-id';
export const SELECTED_WORKSPACE_CHANGED_EVENT =
  'minerva:selected-workspace-changed';
export const WORKSPACE_ID_HEADER = 'x-workspace-id';

function normalizeWorkspaceId(workspaceId?: string | null) {
  return workspaceId?.trim() ?? '';
}

/**
 * Serialized form of the workspace-selection cookie.
 *
 * The cookie is a single browser-wide slot, but a workspace id is only
 * meaningful inside the tenant it was chosen in. A user with accounts in more
 * than one tenant (KOHO Production and KOHO IMT, for example) would otherwise
 * carry tenant A's workspace id into a tenant B session, where it can never
 * prove membership — and since ENG-3203 an unprovable explicit selection fails
 * closed, that turned a stale cookie into a hard error on every
 * workspace-scoped config page.
 *
 * Qualifying the value with the tenant lets the server tell "this selection
 * belongs to another tenant, ignore it" apart from "this selection names a
 * workspace of THIS tenant that the caller may not use", which must still fail
 * closed. A legacy unqualified value stays readable so existing sessions keep
 * their selection across the rollout.
 */
export function formatSelectedWorkspaceCookieValue({
  tenantId,
  workspaceId,
}: {
  tenantId?: string | null;
  workspaceId?: string | null;
}) {
  const normalizedWorkspaceId = normalizeWorkspaceId(workspaceId);

  if (!normalizedWorkspaceId) {
    return '';
  }

  const normalizedTenantId = normalizeWorkspaceId(tenantId);

  return normalizedTenantId
    ? `${normalizedTenantId}:${normalizedWorkspaceId}`
    : normalizedWorkspaceId;
}

/**
 * Parse a selection cookie written by {@link formatSelectedWorkspaceCookieValue}.
 *
 * `tenantId` is empty for a legacy unqualified value, which means the origin
 * tenant is unknown rather than "any tenant" — callers must treat that as
 * unproven rather than as a cross-tenant grant.
 */
export function parseSelectedWorkspaceCookieValue(value?: string | null) {
  const rawValue = value?.trim() ?? '';
  const separatorIndex = rawValue.lastIndexOf(':');

  if (separatorIndex < 0) {
    return { tenantId: '', workspaceId: rawValue };
  }

  return {
    tenantId: rawValue.slice(0, separatorIndex).trim(),
    workspaceId: rawValue.slice(separatorIndex + 1).trim(),
  };
}

/**
 * Value for the `x-workspace-id` request header, read from the same
 * browser-wide storage the cookie uses.
 *
 * The header is the second channel a browser states workspace intent on, and
 * callers that have no tenant in scope read it un-keyed — so it can carry a
 * workspace chosen in a DIFFERENT tenant. Unstamped, the server takes that as
 * an explicit selection for the acting tenant, where it cannot prove membership
 * and fails closed: the same cross-tenant breakage the cookie stamp fixes,
 * surfacing as a 403 rather than a 500.
 *
 * Stamping it with the tenant the selection was made in lets the server ignore
 * it in another tenant, exactly as it does for the cookie. `writeSelected...`
 * records that tenant so this stays correct even for un-keyed reads.
 */
export function readSelectedWorkspaceHeaderValue(tenantId?: string | null) {
  if (typeof window === 'undefined') {
    return '';
  }

  const workspaceId = readSelectedWorkspaceIdSnapshot(tenantId);

  if (!workspaceId) {
    return '';
  }

  let originTenantId = tenantId?.trim() ?? '';

  if (!originTenantId) {
    try {
      originTenantId =
        window.localStorage
          .getItem(SELECTED_WORKSPACE_TENANT_STORAGE_KEY)
          ?.trim() ?? '';
    } catch {
      originTenantId = '';
    }
  }

  // Send NOTHING rather than an unstamped id when the origin tenant is unknown.
  //
  // A session that chose its workspace before the tenant key existed has the
  // browser-wide id but no record of whose it is. Sending it bare would state an
  // explicit selection the server must then prove, and in another tenant that
  // proof fails closed — the exact cross-tenant breakage this stamp exists to
  // prevent, and one that self-deadlocks, because the workspace-list request
  // that would repair the state is itself scope-resolved and refused.
  //
  // Omitting the header is safe and strictly better: the server falls back to
  // the tenant's default workspace exactly as a first visit does, and the
  // provider then writes a properly stamped selection, so the session heals on
  // its next load instead of requiring the user to log out.
  if (!originTenantId) {
    return '';
  }

  return formatSelectedWorkspaceCookieValue({
    tenantId: originTenantId,
    workspaceId,
  });
}

function getTenantWorkspaceStorageKey(tenantId?: string | null) {
  const normalizedTenantId = tenantId?.trim();

  return normalizedTenantId
    ? `${SELECTED_WORKSPACE_ID_STORAGE_KEY}:${normalizedTenantId}`
    : SELECTED_WORKSPACE_ID_STORAGE_KEY;
}

export function readSelectedWorkspaceIdSnapshot(tenantId?: string | null) {
  if (typeof window === 'undefined') {
    return '';
  }

  try {
    const normalizedTenantId = tenantId?.trim();
    const tenantWorkspaceId = normalizeWorkspaceId(
      window.localStorage.getItem(getTenantWorkspaceStorageKey(tenantId))
    );

    if (normalizedTenantId) {
      return tenantWorkspaceId;
    }

    return (
      tenantWorkspaceId ||
      normalizeWorkspaceId(
        window.localStorage.getItem(SELECTED_WORKSPACE_ID_STORAGE_KEY)
      )
    );
  } catch {
    return '';
  }
}

/**
 * Read the selection cookie this browser is currently sending.
 *
 * The provider uses this to detect a cookie that is stale or missing its tenant
 * stamp. localStorage is already tenant-keyed and therefore stays correct on
 * its own, so comparing only against localStorage would leave a legacy or
 * wrong-tenant cookie in place indefinitely — and that cookie is the value the
 * SERVER reads. Existing sessions must be repaired on their next page load
 * rather than requiring the user to switch workspaces or clear cookies.
 */
export function readSelectedWorkspaceCookieValue() {
  if (typeof document === 'undefined') {
    return '';
  }

  for (const pair of document.cookie.split(';')) {
    const separatorIndex = pair.indexOf('=');

    if (separatorIndex < 0) {
      continue;
    }

    if (
      pair.slice(0, separatorIndex).trim() !== SELECTED_WORKSPACE_COOKIE_NAME
    ) {
      continue;
    }

    try {
      return decodeURIComponent(pair.slice(separatorIndex + 1).trim());
    } catch {
      return '';
    }
  }

  return '';
}

/**
 * Drop the selection cookie when it cannot belong to `tenantId`.
 *
 * This exists for one deadlock. A cookie left by another tenant — or a legacy
 * one carrying no tenant stamp — fails membership proof, so the workspace-list
 * request, which is itself workspace-scoped, is refused; the list stays empty
 * and no selection ever resolves. Any repair gated on a resolved selection is
 * therefore unreachable for exactly the sessions that need it, and the user's
 * only way out is a log-out.
 *
 * It deliberately touches ONLY the cookie, and only one that is already
 * unusable here. localStorage is tenant-keyed and still correct, and clearing
 * it would destroy a valid selection during the hydration frame, when the
 * browser snapshot has not been published yet and no selection has resolved.
 *
 * Returns whether a cookie was cleared.
 */
export function clearUnusableSelectedWorkspaceCookie(tenantId?: string | null) {
  const normalizedTenantId = normalizeWorkspaceId(tenantId);
  const cookieValue = readSelectedWorkspaceCookieValue();

  if (!normalizedTenantId || !cookieValue) {
    return false;
  }

  const { tenantId: cookieTenantId } =
    parseSelectedWorkspaceCookieValue(cookieValue);

  if (cookieTenantId === normalizedTenantId) {
    return false;
  }

  writeSelectedWorkspaceCookie('');

  return true;
}

function writeSelectedWorkspaceCookie(cookieValue: string) {
  if (typeof document === 'undefined') {
    return;
  }

  const secure =
    typeof window !== 'undefined' && window.location.protocol === 'https:'
      ? '; Secure'
      : '';

  if (!cookieValue) {
    document.cookie = `${SELECTED_WORKSPACE_COOKIE_NAME}=; Path=/; Max-Age=0; SameSite=Lax${secure}`;
    return;
  }

  document.cookie = `${SELECTED_WORKSPACE_COOKIE_NAME}=${encodeURIComponent(
    cookieValue
  )}; Path=/; Max-Age=31536000; SameSite=Lax${secure}`;
}

export function writeSelectedWorkspaceIdSnapshot({
  tenantId,
  workspaceId,
}: {
  tenantId?: string | null;
  workspaceId?: string | null;
}) {
  const normalizedWorkspaceId = normalizeWorkspaceId(workspaceId);

  if (typeof window !== 'undefined') {
    try {
      const tenantStorageKey = getTenantWorkspaceStorageKey(tenantId);

      if (normalizedWorkspaceId) {
        window.localStorage.setItem(tenantStorageKey, normalizedWorkspaceId);
        window.localStorage.setItem(
          SELECTED_WORKSPACE_ID_STORAGE_KEY,
          normalizedWorkspaceId
        );
        // Record whose workspace the un-keyed value holds, so an un-keyed
        // reader can stamp it and the server can ignore it in another tenant.
        const normalizedTenantId = normalizeWorkspaceId(tenantId);

        if (normalizedTenantId) {
          window.localStorage.setItem(
            SELECTED_WORKSPACE_TENANT_STORAGE_KEY,
            normalizedTenantId
          );
        } else {
          window.localStorage.removeItem(SELECTED_WORKSPACE_TENANT_STORAGE_KEY);
        }
      } else {
        window.localStorage.removeItem(tenantStorageKey);
        window.localStorage.removeItem(SELECTED_WORKSPACE_ID_STORAGE_KEY);
        window.localStorage.removeItem(SELECTED_WORKSPACE_TENANT_STORAGE_KEY);
      }

      window.dispatchEvent(
        new CustomEvent(SELECTED_WORKSPACE_CHANGED_EVENT, {
          detail: { workspaceId: normalizedWorkspaceId },
        })
      );
    } catch {
      // Browser storage can be disabled. The cookie write below still lets
      // server-rendered config pages keep the most recent scoped workspace.
    }
  }

  writeSelectedWorkspaceCookie(
    formatSelectedWorkspaceCookieValue({
      tenantId,
      workspaceId: normalizedWorkspaceId,
    })
  );
}

export function clearSelectedWorkspaceState() {
  if (typeof window !== 'undefined') {
    try {
      const storageKeys: string[] = [];

      for (let index = 0; index < window.localStorage.length; index += 1) {
        const key = window.localStorage.key(index);

        if (
          key === SELECTED_WORKSPACE_ID_STORAGE_KEY ||
          key === SELECTED_WORKSPACE_TENANT_STORAGE_KEY ||
          key?.startsWith(`${SELECTED_WORKSPACE_ID_STORAGE_KEY}:`)
        ) {
          storageKeys.push(key);
        }
      }

      for (const key of storageKeys) {
        window.localStorage.removeItem(key);
      }

      window.dispatchEvent(
        new CustomEvent(SELECTED_WORKSPACE_CHANGED_EVENT, {
          detail: { workspaceId: '' },
        })
      );
    } catch {
      // Best-effort logout cleanup: blocked storage should not prevent the
      // auth provider and durable session from being cleared.
    }
  }

  writeSelectedWorkspaceCookie('');
}
